import { Handle, Position, type Node, type NodeProps } from '@xyflow/react'
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'

import { DESIGNER_ICON_SET } from '@flowx/designer/shared/designer-icons'
import { NodeEntity } from '@flowx/designer/shared/entities/node.entity'
import {
  FlxInputContainer,
  FlxInputField,
  MemoFlxIcon,
  MemoFlxInput,
} from '@flowx/react-ui-toolkit'

import { NodeTypeGrouped, nodeTypesGrouped, titleSlug } from '../../../dictionary/nodes-types'

/** RF node type of the transient New Node panel (not backed by any Angular entity) */
export const NEW_NODE_PANEL_TYPE = 'newNodePanel'

export interface NewNodePanelData extends Record<string, unknown> {
  onSelect: (option: NodeTypeGrouped) => void
}

const searchIcon = () => <MemoFlxIcon name="search" />

/**
 * Only free-droppable node types belong in the panel: boundary-event-only types
 * (non-interrupting message/timer, error catch) must land on a node, same rule
 * the palette free-drop applies (#onPaletteDrop's isNodeDefinitionType check).
 */
const creatableNodeGroups = nodeTypesGrouped
  .map((group) => ({
    title: group.title ?? '',
    subnodes: (group.subnodes ?? []).filter((subnode) =>
      NodeEntity.isNodeDefinitionType(subnode.type as string)
    ),
  }))
  .filter((group) => group.subnodes.length > 0)

function CategoryGroup({
  title,
  subnodes,
  onSelect,
}: {
  title: string
  subnodes: NodeTypeGrouped[]
  onSelect: (option: NodeTypeGrouped) => void
}) {
  const [expanded, setExpanded] = useState(true)

  const categorySlug = titleSlug(title)

  return (
    <div
      className="flex flex-col gap-1 py-1"
      data-testid={`bpmn-new-node-category-${categorySlug}`}
    >
      <div className="flex justify-between text-neutral-500">
        <span className="grow flex items-center rounded-l text-[10px] leading-4 font-semibold px-3 py-1 text-neutral-400 bg-neutral-50">
          {title}
        </span>
        <div className="bg-neutral-50 pr-3 rounded-r">
          <button
            onClick={() => setExpanded(!expanded)}
            data-testid={`bpmn-new-node-category-toggle-${categorySlug}`}
          >
            <i
              className={`text-sm font-bold ${expanded ? 'ph ph-caret-up' : 'ph ph-caret-down'}`}
            />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="flex flex-col gap-2">
          {subnodes.map((option) => (
            <button
              key={option.title ?? option.icon}
              className="flex gap-2 items-start hover:bg-neutral-50 active:opacity-75 active:bg-white transition-all duration-150 rounded-lg p-1 text-left"
              onClick={() => onSelect(option)}
              data-testid={`new-node-${titleSlug(option.title)}`}
            >
              <div
                className="flex items-center bg-neutral-50 border border-neutral-100 rounded-lg p-2 text-neutral-700 [&_svg]:h-4 [&_svg]:w-4"
                dangerouslySetInnerHTML={{ __html: DESIGNER_ICON_SET[option.icon] ?? '' }}
              />
              <div className="flex flex-col gap-1">
                <span className="text-neutral-900 font-semibold text-xs leading-4.5">
                  {option.title}
                </span>
                <span className="text-neutral-500 text-[10px] leading-4">{option.description}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Floating "New Node" panel — the BPMN twin of the workflow editor's
 * SelectionNode: shown where a connection drag was dropped on empty lane space,
 * listing the node types that can be created there. Picking one creates the
 * node at the panel position plus the sequence from the drag's source.
 */
function NewNodePanel({ data }: NodeProps<Node<NewNodePanelData>>) {
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [filter, setFilter] = useState('')

  useEffect(() => {
    // Focus the search input when the panel opens, after React Flow settles the node
    const timeoutId = setTimeout(() => searchInputRef.current?.focus(), 100)

    return () => clearTimeout(timeoutId)
  }, [])

  const filteredGroups = useMemo(() => {
    const query = filter.toLowerCase()

    return creatableNodeGroups
      .map((group) => ({
        title: group.title,
        subnodes: group.subnodes.filter(
          (option) =>
            option.title?.toLowerCase().includes(query) ||
            option.description?.toLowerCase().includes(query)
        ),
      }))
      .filter((group) => group.subnodes.length > 0)
  }, [filter])

  return (
    <div
      className="nowheel nopan max-h-[480px] w-96 bg-white flex flex-col gap-3 rounded-xl p-3 shadow-[0_4px_16px_0_rgba(15,17,20,0.1)]"
      data-testid="bpmn-new-node-panel"
    >
      <div className="relative">
        {/* Visible blue dot the preview edge lands on — the workflow editor's
            connected TargetHandle look (SelectionNode) */}
        <Handle
          type="target"
          position={Position.Left}
          isConnectable={false}
          className="!-left-4 pointer-events-none rounded-full ring ring-inset ring-blue-400 bg-blue-400/50 w-3 h-3 border-none"
        />
        <span className="text-neutral-900 text-lg font-bold px-1">New Node</span>
      </div>
      <FlxInputContainer className="flx-ds small">
        <MemoFlxInput suffixIcon={searchIcon}>
          <FlxInputField
            ref={searchInputRef}
            className="flx-ds nopan nowheel nodrag"
            placeholder="Search nodes..."
            onChange={(event: ChangeEvent<HTMLInputElement>) => setFilter(event.target.value ?? '')}
            data-testid="bpmn-new-node-search-input"
          />
        </MemoFlxInput>
      </FlxInputContainer>
      <div className="grow min-h-0 flex flex-col gap-2 overflow-y-auto">
        {filteredGroups.length > 0 ? (
          filteredGroups.map((group) => (
            <CategoryGroup
              key={group.title}
              title={group.title}
              subnodes={group.subnodes}
              onSelect={data.onSelect}
            />
          ))
        ) : (
          <span className="text-neutral-500 text-xs text-center py-4">
            There are no nodes matching your search.
          </span>
        )}
      </div>
    </div>
  )
}

export default NewNodePanel
